import { unByKey } from 'ol/Observable';
import BaseLayer from 'ol/layer/Base';
import { assert } from 'cadenza/utils/custom-error';
import { refreshLayer } from 'cadenza/map/layer-utils';
import { getLayerTasks } from 'cadenza/map/layer-tasks';

import { LayerProperty } from './layer-property';
import { context } from './context';

/**
 * Please do *not* instantiate this directly and instead use cadenza/map/context, i.e. context(layer)
 */
export class Context {

  _layer;
  _imgLoadTaskId;
  _imageLoadTaskRegistered;

  constructor (layer) {
    assert(layer instanceof BaseLayer, 'layer must be an instance of ol/layer/Layer');
    this._layer = layer;
  }

  setup (map) {
    const layer = this._layer;
    this._eventKeys = layer.on(`change:${LayerProperty.VISIBLE}`, () => context(map).push());
    const interval = layer.get(LayerProperty.RELOAD_INTERVAL);
    if (interval > 0) {
      this._intervalId = setInterval(() => refreshLayer(layer), interval);
    }
  }

  teardown () {
    unByKey(this._eventKeys);
    clearInterval(this._intervalId);
  }

  hasPrivilege (privilege) {
    return this._privileges.includes(privilege);
  }

  get _privileges () {
    return this._layer.get('privileges') || [];
  }

  // see ol/layer/Layer#visibleAtResolution()
  getInRange (resolution) {
    const { minResolution, maxResolution } = this._layer.getProperties();
    return (resolution >= minResolution && resolution < maxResolution);
  }

  registerImageLoadTaskHandling () {
    const imgLoadExecutionHelper = this._getImgLoadExecutionHelper();
    if (imgLoadExecutionHelper && !this._imageLoadTaskRegistered) {
      this._imageLoadTaskRegistered = true;
      const source = this._layer.getSource();
      const imageLoadFunction = this._layer.getSource().getImageLoadFunction();
      source.on('imageloadstart', () => {
        this._removeCurrentImageLoadTask();
        this._imgLoadTaskId = getLayerTasks(this._layer).registerTask(
          () => imgLoadExecutionHelper.cancelCurrent(),
          () => refreshLayer(this._layer)
        );
        // use imgLoadTask as cancelId, so that by abort image loading we can use lastAbortCancelId
        // to find and complete the correct task id (see below)
        imageLoadFunction.cancelId = this._imgLoadTaskId;
      });
      this._layer.getSource().on('imageloadend', () => this._removeCurrentImageLoadTask());
      source.on('imageloaderror', () => {
        // We use imageLoadTaskId as cancelId(see above)
        if (imgLoadExecutionHelper.lastAbortCancelId) {
          this._layer.get(LayerProperty.TASKS)?.completeTask(imgLoadExecutionHelper.lastAbortCancelId);
        }
      });
    }
  }

  restartTasks () {
    getLayerTasks(this._layer).restartTasks();
  }

  _removeCurrentImageLoadTask () {
    if (this._imgLoadTaskId) {
      this._layer.get(LayerProperty.TASKS)?.completeTask(this._imgLoadTaskId);
    }
  }

  _getImgLoadExecutionHelper () {
    return this._layer.get(LayerProperty.IMG_LOAD_EXECUTION_HELPER);
  }

}
